import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ThemedButton } from '../ThemedButton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Upload } from 'lucide-react';
import { useWizard } from '../../contexts/WizardContext';
import api from '../../utils/api';
import { toast } from 'sonner';

const ClaimsHistory = () => {
  const { formData, updateFormData, nextStep } = useWizard();
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(formData.claims_files || []);
  
  const { control, handleSubmit, watch, register } = useForm({
    defaultValues: {
      claims_history_flag: formData.claims_history_flag,
      claims_notes: formData.claims_notes,
    }
  });

  const claimsHistoryFlag = watch('claims_history_flag');

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedFiles([...uploadedFiles, response.data.file_id]);
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = (data) => {
    updateFormData({
      ...data,
      claims_files: uploadedFiles
    });
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <Label className="text-base mb-3 block">Claims History</Label>
        <p className="text-sm text-slate-600 mb-4">
          Has the organization had any liability claims including civil lawsuits and incidents, been non-renewed, or had any sexual abuse/molestation incidents or claims in the last 5 years?
        </p>
        
        <Controller
          name="claims_history_flag"
          control={control}
          render={({ field }) => (
            <RadioGroup
              onValueChange={(value) => field.onChange(value === 'true')}
              defaultValue={field.value ? 'true' : 'false'}
              className="mt-3"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="true" id="claims_yes" data-testid="claims-history-yes" />
                <Label htmlFor="claims_yes" className="font-normal cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="false" id="claims_no" data-testid="claims-history-no" />
                <Label htmlFor="claims_no" className="font-normal cursor-pointer">No</Label>
              </div>
            </RadioGroup>
          )}
        />
      </div>

      {claimsHistoryFlag && (
        <>
          <div>
            <Label htmlFor="claims_notes">Please provide details *</Label>
            <Textarea
              id="claims_notes"
              data-testid="claims-notes-textarea"
              {...register('claims_notes', { 
                required: claimsHistoryFlag ? 'Please provide details about claims history' : false 
              })}
              className="mt-1.5 min-h-[120px]"
              placeholder="Provide details about claims, incidents, non-renewals, or any sexual abuse/molestation incidents..."
            />
          </div>

          <div>
            <Label>Upload Loss Run Documents (Optional)</Label>
            <p className="text-sm text-slate-600 mt-1 mb-3">
              If available, please upload your loss run report or related documentation
            </p>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              {uploadedFiles.length > 0 ? (
                <div>
                  <p className="text-sm text-slate-900 mb-2">
                    {uploadedFiles.length} file(s) uploaded
                  </p>
                  <ThemedButton
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setUploadedFiles([])}
                  >
                    Clear Files
                  </ThemedButton>
                </div>
              ) : (
                <>
                  <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                  <input
                    type="file"
                    id="file_upload"
                    data-testid="file-upload-input"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.xlsx,.xls"
                    className="hidden"
                  />
                  <label htmlFor="file_upload">
                    <ThemedButton
                      type="button"
                      variant="outline"
                      disabled={uploading}
                      asChild
                    >
                      <span className="cursor-pointer">
                        {uploading ? 'Uploading...' : 'Choose File'}
                      </span>
                    </ThemedButton>
                  </label>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <ThemedButton 
        type="submit"
        data-testid="claims-history-next-btn"
        variant="wizard" size="full"
      >
        Next: Fraud Warranty
      </ThemedButton>
    </form>
  );
};

export default ClaimsHistory;